import { FC, useMemo, useRef, useState, useEffect, CSSProperties, useContext, isValidElement, cloneElement, ReactNode } from "react";
// 组件
import { HolderOutlined, MoreOutlined, FullscreenOutlined, SyncOutlined, CopyOutlined, DeleteOutlined, EditOutlined, } from "@ant-design/icons";
import { ProDropdown, ProModal, ProTooltip, } from "../antd";
import DragSize from "./DragSize";
// 方法
import classNames from "classnames";
import { DragDropContext, DragDropDispatchContext } from "./context/dragDropContext";
import { checkCoincidence, getChangeSize, getGridUseData, getMovePosition, setDragPosition } from "./util";
// 常量
import { COPY_KEY, DELETE_KEY, DragDropOption, DragItemSize, EDIT_KEY, FULL_SCREEN_KEY, MoveData, OptionItemProps, REFRESH_KEY, SizeChangeData, } from "./interface";
import { IBtnList } from "../ButtonGroup/buttonGroup";
import { DashboardComponentActionType, DashboardComponentBaseProps } from "../constants/interface";

/** 拖动项 */
const OptionItem: FC<OptionItemProps> = props => {
  const {
    name, left = 0, top = 0, column = 1, row = 1, minColumn = 1, minRow = 1, otherOperations, isMove,
    previewRef, isDelete, isChangeSize, moveIcon, container, title, subTitle, beforeNode, afterNode,
    fullScreen, refresh, isCopy, isEdit,
  } = props;
  const { width, height, grid, dragItems, dragDropRef, dropfullScreenRef, onClickOperation, onClickOtherOperation, } = useContext(DragDropContext);
  const dispatch = useContext(DragDropDispatchContext);
  const [dragging, setDragging] = useState<boolean>(false); // 是否处于拖动中
  const itemRef = useRef<HTMLDivElement>(null); //拖动项对象  
  const containerRef = useRef<DashboardComponentActionType>();
  useEffect(() => {
    const newStyle = setDragPosition(width, height, {left, top, column, row});
    handleChangeItemStyle(newStyle);
  }, [left, top, column, row, width, height]);
  /** item样式改变 */
  const handleChangeItemStyle = (changeStyle: CSSProperties) => {
    const itemDom = itemRef.current;
    if (!itemDom) return;
    for (let [key, value] of Object.entries(changeStyle)) {
      itemDom.style[key as any] = typeof value === 'number' ? `${value}px` : value;
    }
  };
  /** 鼠标按下 */
  const handleMouseDown = (downEvent: any) => {
    const itemDom = itemRef.current;
    if (!isMove) return;
    if (!itemDom) return;
    const itemSize: DragItemSize = {left, top, column, row};
    const startData: MoveData = {
      x: downEvent.clientX,
      y: downEvent.clientY,
    };
    const startPosition = setDragPosition(width, height, itemSize);
    setDragging(true);
    previewRef.current?.show(startPosition);
    let newPosition = getMovePosition(width, height, column, startPosition.left, startPosition.top);
    const handleMouseMove = (moveEvent: any) => {
      const moveData: MoveData = {
        x: moveEvent.clientX - startData.x,
        y: moveEvent.clientY - startData.y,
      };
      const newLeft = (startPosition.left as number) + moveData.x;
      const newTop = (startPosition.top as number) + moveData.y;
      // 校验是重合情况
      const nextPosition = getMovePosition(width, height, column, newLeft, newTop);
      const movedConfig: DragDropOption = { name, column, row, left: nextPosition.left, top: nextPosition.top, };
      const isCoincidence = checkCoincidence(movedConfig, dragItems);
      if (!isCoincidence) {
        newPosition = {...nextPosition};
        const previewStyle = setDragPosition(width, height, {...itemSize, ...newPosition});
        previewRef.current?.move(previewStyle);
      }
      itemDom.style.left = `${newLeft}px`;
      itemDom.style.top = `${newTop}px`;
    };
    const handleMouseup = () => {
      setDragging(false);
      dispatch?.({ type: 'moveDragItems', payload: { updataName: name, updataConfig: { ...newPosition, column, row, }, } });
      if (newPosition.top === top && newPosition.left === left) {
        const newStyle = setDragPosition(width, height, {left, top, column, row});
        handleChangeItemStyle(newStyle);
      }
      previewRef.current?.hide();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseup);
    };
    window.addEventListener('mousemove', handleMouseMove, false);
    window.addEventListener('mouseup', handleMouseup, false);
  };
  /** 尺寸改变开始 */
  const handleChangeStart = () => {
    setDragging(true);
    const itemSize: DragItemSize = {left, top, column, row};
    const startPosition = setDragPosition(width, height, itemSize);
    previewRef.current?.show(startPosition);
  };
  /** 尺寸改变过程中 */
  const handleChangeMove = (data: SizeChangeData) => {
    const itemSize: DragItemSize = {left, top, column, row};
    const previewStyle = setDragPosition(width, height, getChangeSize(data, itemSize));
    previewRef.current?.move(previewStyle);
  };
  /** 尺寸改变结束 */
  const handleChangeEnd = (data: SizeChangeData) => {
    setDragging(false);
    const itemSize: DragItemSize = {left, top, column, row};
    const newItemSize = getChangeSize(data, itemSize);
    dispatch?.({ type: 'updataDragItems', payload: { updataName: name, updataConfig: newItemSize, }, });
    if (newItemSize.top === top && newItemSize.left === left && newItemSize.column === column && newItemSize.row === row) {
      const newStyle = setDragPosition(width, height, newItemSize);
      handleChangeItemStyle(newStyle);
    }
    previewRef.current?.hide();
  };
  /** 点击操作按钮 */
  const handleMenuClick = (key: string) => {
    if (key === FULL_SCREEN_KEY) {
      return dropfullScreenRef?.current?.show(name);
    }
    if (key === DELETE_KEY) {
      ProModal.confirm({
        title: `确认删除部件 ${title ?? ''}？`,
        onOk: () => {
          dispatch?.({ type: 'deleteDragItem', payload: { deleteName: name, }, });
        },
      });
      return;
    }
    onClickOperation?.(key, props);
  };
  /** 除当前项外其他网格的占用情况 */
  const otherGridUseData = useMemo(() => {
    const otherDragItems = dragItems.filter(item => item.name !== name);
    return getGridUseData(otherDragItems);
  }, [dragItems, name]);
  /** 操作按钮 */
  const interOperations = useMemo(() => {
    const newOperations: IBtnList[] = [];
    if (isCopy) {
      newOperations.unshift({label: '复制', key: COPY_KEY, icon: <CopyOutlined />});
    }
    if (isEdit) {
      newOperations.push({label: '编辑', key: EDIT_KEY, icon: <EditOutlined />});
    }
    if (!isMove) {
      if (refresh) {
        newOperations.unshift({label: '刷新', key: REFRESH_KEY, icon: <SyncOutlined />});
      }
      if (fullScreen) {
        newOperations.unshift({label: '全屏', key: FULL_SCREEN_KEY, icon: <FullscreenOutlined />});
      }
    } else {
      if (isDelete) {
        newOperations.push({label: '删除', key: DELETE_KEY, danger: true, icon: <DeleteOutlined />});
      }
    }
    return newOperations;
  }, [fullScreen, refresh, isMove, isCopy]);
  const itemClasses = useMemo(() => {
    return classNames('drag-drop-item', {
      'drag-drop-item-moving': dragging,
    });
  }, [dragging]);
  const containerClasses = useMemo(() => {
    return classNames('layout-container', {
      'item-move': isMove,
    });
  }, [isMove]);
  const renderContainer = () => {
    if (isValidElement(container)) {
      const childElement = container as React.FunctionComponentElement<DashboardComponentBaseProps>;
      if (dragDropRef) {
        dragDropRef.current[name] = containerRef;
      }
      return cloneElement(childElement, { actionRef: containerRef, })
    }
    return container;
  }
  return (
    <div
      ref={itemRef}
      className={itemClasses}
    >
      <div className='drag-drop-item-layout'>
        {
          isMove && isChangeSize && (
            <DragSize
              itemRef={itemRef}
              width={width}
              height={height}
              left={left}
              top={top}
              column={column}
              row={row}
              grid={grid}
              minRow={minRow}
              minColumn={minColumn}
              otherGridUseData={otherGridUseData}
              onChangeStart={handleChangeStart}
              onChangeMove={handleChangeMove}
              onChangeEnd={handleChangeEnd}
            />
          )
        }
        <div
          className={containerClasses}
          onMouseDown={handleMouseDown}
        >
          <div className="item-header">
            <div className="item-header-left">
              { isMove && !!moveIcon && <div className="item-header-left-icon">{moveIcon}</div> }
              { !!beforeNode && <div className="item-header-left-before">{beforeNode}</div> }
              { !!title && <div className="item-header-left-title">{title}</div> }
              { !!subTitle && <div className="item-header-left-sub-title">{subTitle}</div> }
              { !!afterNode && <div className="item-header-left-after">{afterNode}</div> }
            </div>
            <div className="item-header-right">
              {
                !!otherOperations?.length && (
                  <div className="item-header-right-operation">
                    {
                      otherOperations.map(item => {
                        return (
                          <ProTooltip title={item.label} key={item.key}>
                            <div
                              className="item-header-right-operation-icon"
                              onClick={() => onClickOtherOperation?.(item.key, props)}
                            >
                              {item.icon}
                            </div>
                          </ProTooltip>
                        );
                      })
                    }
                  </div>
                )
              }
              {
                !!interOperations?.length && (
                  <ProDropdown
                    menu={{
                      items: interOperations,
                      onClick: ({key}) => handleMenuClick(key),
                    }}
                    trigger={['click']}
                  >
                    <MoreOutlined className="item-header-right-icon" />
                  </ProDropdown>
                )
              }
            </div>
          </div>
          <div className="item-body">
            {renderContainer()}
          </div>
        </div>
      </div>
    </div>
  );
};

OptionItem.defaultProps = {
  moveIcon: <HolderOutlined />,
  moreIcon: <MoreOutlined />,
  fullScreen: true,
  refresh: true,
  isCopy: true,
  isDelete: true,
  isEdit: true,
};

export default OptionItem;
